/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React from 'react';
import './table.scss';
import { GridDashboardChartItem, HistoricalUtrs } from '@g17eco/types/insight-custom-dashboard';
import { getLatestActualUtrvs, getVariablesWithValues } from './utils';
import { compareMathScope, pattern, tryCalculation } from '../../../../../utils/formula';
import { QuestionReference } from '../../../question-reference/QuestionReference';
import { TakenFromText } from '../common/TakenFromText';
import { SurveyActionData } from '@g17eco/types/surveyAction';

const greenIcon = <i className='fa fa-check text-ThemeSuccessMedium mr-3' />;
const redIcon = <i className='fa fa-times text-ThemeDangerMedium mr-3' />;

interface Column {
  value: boolean;
  name: string;
  utrCode: string;
}

type Props = Pick<GridDashboardChartItem, 'calculation' | 'variables'> & {
  utrsData: HistoricalUtrs[];
  initiativeId: string;
  survey?: Pick<SurveyActionData, '_id' | 'fragmentUniversalTrackerValues'>;
};

const getChartData = ({ utrsData, variables, calculation }: Required<Omit<Props, 'initiativeId' | 'survey'>>) => {
  const { latestActualUtrvs = [], effectiveDate, period, aggregated } = getLatestActualUtrvs(utrsData);

  const variablesWithValues = getVariablesWithValues({ utrsData, variables, utrvs: latestActualUtrvs });

  const chartData = calculation.values.map((value) => {
    const variable = value.formula?.match(pattern)?.[0].slice(1, 2); // compare({a}, 'eq', 1) -> a

    return {
      name: value.name,
      value: tryCalculation({
        formula: value.formula || '',
        variables: variablesWithValues,
        fallback: 0,
        mathScope: compareMathScope,
      }),
      utrCode: variables[variable ?? ''].code,
    };
  });
  return { chartData, effectiveDate, period, aggregated };
};

export const Table = ({ utrsData, variables, calculation, initiativeId, survey }: Props) => {
  if (!utrsData.length || !variables || !calculation) {
    return null;
  }

  const { chartData, effectiveDate, period, aggregated } = getChartData({ utrsData, variables, calculation });

  const TableHeader = () => (
    <th className='d-flex justify-content-between'>
      {(calculation.headers ?? []).map((header) =>
        'name' in header ? <span key={header.name}>{header.name}</span> : null
      )}
    </th>
  );

  // Make 2 columns table.
  const rows = chartData.reduce((rows, value, index) => {
    const isEvenRow = index % 2 === 0;
    const row = isEvenRow ? [value] : [...rows[index - 1], value];
    rows[isEvenRow ? index : index - 1] = row;
    return rows;
  }, [] as Column[][]);

  return (
    <>
      <table className='insight-dashboard__table w-100 mt-2'>
        <thead>
          <tr>
            <TableHeader />
            <th className='spacer'></th>
            <TableHeader />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((column, index) => (
                <React.Fragment key={index}>
                  <td>
                    <QuestionReference
                      initiativeId={initiativeId}
                      survey={survey}
                      utrsCodes={[column.utrCode]}
                      utrsData={utrsData}
                      getName={() => getColumn(column)}
                      btnClass='text-decoration-none'
                      tooltip=''
                    />
                  </td>
                  {index % 2 === 0 ? <th className='spacer'></th> : null}
                </React.Fragment>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <TakenFromText effectiveDate={effectiveDate} period={period} aggregated={aggregated} />
    </>
  );
};

function getColumn(column: Column) {
  return (
    <div className={'text px-2 py-3'}>
      <span className={'mr-2'}>{column.name}</span>
      <span>{column.value ? greenIcon : redIcon}</span>
    </div>
  );
}
